import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, EyeOff, Star, Loader2, X, GripVertical, Image as ImageIcon, Film } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { MultiImageUploader } from '@/components/ImageUploader';
import VideoUploader from '@/components/VideoUploader';

// Sortable Media Item Component
function SortableMediaItem({ item, onRemove, onUpdateAlt, isSelected, onSelect }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group bg-muted rounded-sm overflow-hidden ${isDragging ? 'z-50' : ''} ${isSelected ? 'ring-2 ring-primary' : ''}`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 z-10 p-1.5 bg-black/50 text-white rounded cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <GripVertical size={14} />
      </div>

      {/* Remove Button */}
      <button
        type="button"
        onClick={() => onRemove(item.id)}
        className="absolute top-2 right-2 z-10 p-1.5 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
      >
        <X size={14} />
      </button>

      {/* Cover Badge */}
      {isSelected && (
        <div className="absolute bottom-12 left-2 z-10 px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded">
          ★ Copertina
        </div>
      )}

      {/* Media Preview */}
      <div className="aspect-square cursor-pointer" onClick={() => onSelect(item)}>
        {item.type === 'video' ? (
          <div className="w-full h-full relative flex items-center justify-center bg-gray-800">
            {item.thumbnail_url && (
              <img src={item.thumbnail_url} alt={item.alt_text || 'Video'} className="absolute inset-0 w-full h-full object-cover opacity-80" />
            )}
            <Film size={24} className="relative text-white drop-shadow" />
          </div>
        ) : (
          <img
            src={item.thumbnail_url || item.url}
            alt={item.alt_text || 'Media'}
            className="w-full h-full object-cover"
          />
        )}
      </div>

      {/* Alt Text Input */}
      <div className="p-2">
        <Input
          value={item.alt_text || ''}
          onChange={(e) => onUpdateAlt(item.id, e.target.value)}
          placeholder="Alt text *"
          className="text-xs h-7"
        />
      </div>
    </div>
  );
}

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '',
    tags: '',
    location: '',
    date: '',
    is_featured: false,
    is_published: true,
    cover_image: '',
    media: []
  });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, categoriesRes] = await Promise.all([
        api.get('/admin/projects'),
        api.get('/categories')
      ]);
      setProjects(projectsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        title: project.title,
        description: project.description || '',
        category_id: project.category_id,
        tags: project.tags?.join(', ') || '',
        location: project.location || '',
        date: project.date || '',
        is_featured: project.is_featured,
        is_published: project.is_published,
        cover_image: project.cover_image || '',
        media: project.media || []
      });
    } else {
      setEditingProject(null);
      setFormData({
        title: '',
        description: '',
        category_id: categories[0]?.id || '',
        tags: '',
        location: '',
        date: '',
        is_featured: false,
        is_published: true,
        cover_image: '',
        media: []
      });
    }
    setDialogOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle media from MultiImageUploader
  const handleMediaChange = (newMedia) => {
    setFormData(prev => {
      const updatedData = { ...prev, media: newMedia };
      // Auto-set cover if not set and media exists
      if (!prev.cover_image && newMedia.length > 0) {
        updatedData.cover_image = newMedia[0].url;
      }
      return updatedData;
    });
  };

  // DnD Handler
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setFormData(prev => {
        const oldIndex = prev.media.findIndex(item => item.id === active.id);
        const newIndex = prev.media.findIndex(item => item.id === over.id);
        const newMedia = arrayMove(prev.media, oldIndex, newIndex).map((item, idx) => ({
          ...item,
          order: idx
        }));
        return { ...prev, media: newMedia };
      });
    }
  };

  const removeMedia = (mediaId) => {
    setFormData(prev => {
      const newMedia = prev.media.filter(m => m.id !== mediaId);
      // Reset cover if removed image was cover
      const removedItem = prev.media.find(m => m.id === mediaId);
      let newCover = prev.cover_image;
      if (removedItem && prev.cover_image === removedItem.url) {
        newCover = newMedia.length > 0 ? newMedia[0].url : '';
      }
      return {
        ...prev,
        media: newMedia,
        cover_image: newCover
      };
    });
  };

  const updateMediaAlt = (mediaId, altText) => {
    setFormData(prev => ({
      ...prev,
      media: prev.media.map(m => m.id === mediaId ? { ...m, alt_text: altText } : m)
    }));
  };

  const setCoverImage = (item) => {
    if (item.type === 'video') {
      if (item.thumbnail_url) setFormData(prev => ({ ...prev, cover_image: item.thumbnail_url.replace('/thumbnails/', '/images/').replace('_thumb.', '_medium.') }));
      else toast.info('Scegli una foto come copertina');
      return;
    }
    setFormData(prev => ({ ...prev, cover_image: item.url }));
  };

  // Add uploaded video (stored on the site)
  const addUploadedVideo = (video) => {
    setFormData(prev => ({
      ...prev,
      media: [...prev.media, {
        id: video.id,
        url: video.url,
        thumbnail_url: video.thumbnail_url || '',
        type: 'video',
        alt_text: video.alt_text || 'Video',
        embed_url: '',
        order: prev.media.length
      }]
    }));
  };

  // Add video by URL
  const addVideoUrl = () => {
    const newId = `video_${Date.now()}`;
    setFormData(prev => ({
      ...prev,
      media: [...prev.media, {
        id: newId,
        url: '',
        thumbnail_url: '',
        type: 'video',
        alt_text: '',
        embed_url: '',
        order: prev.media.length
      }]
    }));
  };

  const updateVideoUrl = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      media: prev.media.map(m => m.id === id ? { ...m, [field]: value } : m)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.category_id) {
      toast.error('Compila titolo e categoria');
      return;
    }

    // Validate that all media have alt_text
    const missingAlt = formData.media.filter(m => m.url && !m.alt_text);
    if (missingAlt.length > 0) {
      toast.error(`Aggiungi alt text a tutte le immagini (${missingAlt.length} mancanti)`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
        media: formData.media.filter(m => m.url) // Remove empty URL items
      };

      if (editingProject) {
        await api.put(`/admin/projects/${editingProject.id}`, payload);
        toast.success('Progetto aggiornato');
      } else {
        await api.post('/admin/projects', payload);
        toast.success('Progetto creato');
      }

      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo progetto?')) return;

    try {
      await api.delete(`/admin/projects/${projectId}`);
      toast.success('Progetto eliminato');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  const getCategoryName = (categoryId) => {
    return categories.find(c => c.id === categoryId)?.name || 'N/A';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-projects-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl mb-2">Progetti</h1>
          <p className="text-muted-foreground">Gestisci il tuo portfolio</p>
        </div>
        <Button onClick={() => openDialog()} data-testid="new-project-btn">
          <Plus size={16} className="mr-2" />
          Nuovo Progetto
        </Button>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <motion.div
            key={project.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border overflow-hidden group"
            data-testid={`project-item-${project.id}`}
          >
            {/* Cover Image */}
            <div className="aspect-video bg-muted relative">
              {project.cover_image ? (
                <img
                  src={project.cover_image}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  <ImageIcon size={32} />
                </div>
              )}
              {/* Status Badges */}
              <div className="absolute top-2 right-2 flex gap-2">
                {project.is_featured && (
                  <span className="bg-yellow-500 text-white px-2 py-0.5 text-xs flex items-center gap-1">
                    <Star size={12} fill="currentColor" />
                    Featured
                  </span>
                )}
                {!project.is_published && (
                  <span className="bg-gray-500 text-white px-2 py-0.5 text-xs flex items-center gap-1">
                    <EyeOff size={12} />
                    Bozza
                  </span>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              <h3 className="font-heading text-lg mb-1">{project.title}</h3>
              <p className="text-sm text-muted-foreground mb-2">
                {getCategoryName(project.category_id)} • {project.location || 'N/A'}
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                {project.media?.length || 0} immagini
              </p>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openDialog(project)}
                  data-testid={`edit-project-${project.id}`}
                >
                  <Edit size={14} className="mr-1" />
                  Modifica
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(project.id)}
                  className="text-destructive hover:text-destructive"
                  data-testid={`delete-project-${project.id}`}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {projects.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">
          <p className="mb-4">Nessun progetto ancora.</p>
          <Button onClick={() => openDialog()}>Crea il primo progetto</Button>
        </div>
      )}

      {/* Project Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProject ? 'Modifica Progetto' : 'Nuovo Progetto'}</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="info" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="info">Informazioni</TabsTrigger>
              <TabsTrigger value="media">Immagini ({formData.media.length})</TabsTrigger>
            </TabsList>

            {/* Info Tab */}
            <TabsContent value="info" className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block">Titolo *</Label>
                  <Input
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Es: Matrimonio sul Lago di Como"
                    data-testid="project-title-input"
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Categoria *</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category_id: value }))}
                  >
                    <SelectTrigger data-testid="project-category-select">
                      <SelectValue placeholder="Seleziona categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Descrizione</Label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Racconta il progetto..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block">Location</Label>
                  <Input
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Es: Lago di Como, Milano"
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Data</Label>
                  <Input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Tags (separati da virgola)</Label>
                <Input
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  placeholder="matrimonio, lago, romantico, elegante"
                />
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_published}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))}
                    data-testid="project-published-switch"
                  />
                  <Label>Pubblicato</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))}
                    data-testid="project-featured-switch"
                  />
                  <Label>In evidenza (homepage)</Label>
                </div>
              </div>
            </TabsContent>

            {/* Media Tab */}
            <TabsContent value="media" className="space-y-6">
              {/* Upload Zone */}
              <MultiImageUploader
                value={formData.media}
                onChange={handleMediaChange}
                label="Carica Immagini"
                maxImages={100}
                maxSize={20}
              />

              {/* Video: upload or external URL */}
              <div className="flex flex-wrap items-start gap-3">
                <VideoUploader onUploaded={addUploadedVideo} label="Carica Video (MP4/MOV, max 300MB)" testId="project-video-upload-btn" />
                <Button type="button" variant="ghost" size="sm" onClick={addVideoUrl} data-testid="project-video-url-btn">
                  <Film size={14} className="mr-1" />
                  Oppure link YouTube/Vimeo
                </Button>
              </div>

              {/* Media Grid with Drag & Drop */}
              {formData.media.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <Label>Galleria ({formData.media.length} elementi) - Clicca per impostare copertina, trascina per riordinare</Label>
                  </div>

                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={formData.media.map(m => m.id)}
                      strategy={rectSortingStrategy}
                    >
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {formData.media.map((item) => (
                          item.type === 'video' && !item.url?.startsWith('/api') ? (
                            // Video URL input
                            <div key={item.id} className="border border-border p-3 rounded-sm space-y-2 col-span-1">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Film size={12} /> Video
                                </span>
                                <button
                                  type="button"
                                  onClick={() => removeMedia(item.id)}
                                  className="text-destructive hover:text-destructive/80"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                              <Input
                                value={item.url}
                                onChange={(e) => updateVideoUrl(item.id, 'url', e.target.value)}
                                placeholder="URL thumbnail"
                                className="text-xs h-7"
                              />
                              <Input
                                value={item.embed_url || ''}
                                onChange={(e) => updateVideoUrl(item.id, 'embed_url', e.target.value)}
                                placeholder="Embed URL (YouTube/Vimeo)"
                                className="text-xs h-7"
                              />
                              <Input
                                value={item.alt_text}
                                onChange={(e) => updateVideoUrl(item.id, 'alt_text', e.target.value)}
                                placeholder="Descrizione *"
                                className="text-xs h-7"
                              />
                            </div>
                          ) : (
                            <SortableMediaItem
                              key={item.id}
                              item={item}
                              onRemove={removeMedia}
                              onUpdateAlt={updateMediaAlt}
                              isSelected={formData.cover_image === item.url}
                              onSelect={setCoverImage}
                            />
                          )
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              )}

              {formData.media.length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  Nessuna immagine. Usa il box sopra per caricare le foto del progetto.
                </p>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="save-project-btn">
              {saving ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                'Salva Progetto'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
